import type {
  DocBlock,
  DocField,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  paragraphsOrPrompt,
  splitItems,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A law-school case brief. Two layouts over the same facts: the classic brief (facts,
 * procedural history, issue, holding, rule, reasoning, disposition, separate opinions,
 * notes) and FIRAC, which folds them into Facts, Issue, Rule, Application, Conclusion.
 */

export type CaseBriefLayout = "standard" | "firac";

export interface CaseBriefInput {
  layout: CaseBriefLayout;
  caseName: string;
  citation: string;
  court: string;
  year: string;
  facts: string;
  history: string;
  /** One issue per line, each phrased as a question. */
  issues: string;
  holding: string;
  rule: string;
  reasoning: string;
  disposition: string;
  separate: string;
  notes: string;
}

export interface CaseBriefLabels {
  defaultTitle: string;
  subtitle: { standard: string; firac: string };
  fields: { caseName: string; citation: string; court: string; year: string };
  citationNote: string;
  headings: {
    facts: string;
    history: string;
    issues: string;
    holding: string;
    rule: string;
    reasoning: string;
    disposition: string;
    separate: string;
    notes: string;
    firacFacts: string;
    firacIssue: string;
    firacRule: string;
    firacApplication: string;
    firacConclusion: string;
  };
  prompts: {
    facts: string;
    history: string;
    issues: string;
    holding: string;
    rule: string;
    reasoning: string;
    disposition: string;
    separate: string;
    notes: string;
  };
  issueGuidance: string;
}

function caseFields(input: CaseBriefInput, labels: CaseBriefLabels): DocBlock {
  const name = input.caseName.trim();
  const fields: DocField[] = [
    {
      label: labels.fields.caseName,
      value: name ? [{ text: name, italic: true }] : undefined,
    },
    { label: labels.fields.citation, value: input.citation.trim() },
    { label: labels.fields.court, value: input.court.trim() },
    { label: labels.fields.year, value: input.year.trim() },
  ];
  return { kind: "fields", fields };
}

function issueBlocks(
  input: CaseBriefInput,
  labels: CaseBriefLabels
): DocBlock[] {
  const issues = splitItems(input.issues);
  if (issues.length === 0) {
    return [{ kind: "prompt", text: labels.prompts.issues }];
  }
  const list: DocBlock = {
    kind: "list",
    list: {
      style: issues.length > 1 ? "decimal" : "bullet",
      items: issues.map((text) => ({ text })),
    },
  };
  return [list, { kind: "guidance", text: labels.issueGuidance }];
}

function part(text: string, prompt: string, heading: string): DocSection {
  return { heading, blocks: paragraphsOrPrompt(text, prompt) };
}

function standard(
  input: CaseBriefInput,
  labels: CaseBriefLabels
): DocSection[] {
  const { headings, prompts } = labels;
  return [
    part(input.facts, prompts.facts, headings.facts),
    part(input.history, prompts.history, headings.history),
    { heading: headings.issues, blocks: issueBlocks(input, labels) },
    part(input.holding, prompts.holding, headings.holding),
    part(input.rule, prompts.rule, headings.rule),
    part(input.reasoning, prompts.reasoning, headings.reasoning),
    part(input.disposition, prompts.disposition, headings.disposition),
    part(input.separate, prompts.separate, headings.separate),
    part(input.notes, prompts.notes, headings.notes),
  ];
}

function firac(input: CaseBriefInput, labels: CaseBriefLabels): DocSection[] {
  const { headings, prompts } = labels;
  return [
    {
      heading: headings.firacFacts,
      blocks: [
        ...paragraphsOrPrompt(input.facts, prompts.facts),
        { kind: "subheading", text: headings.history },
        ...paragraphsOrPrompt(input.history, prompts.history),
      ],
    },
    { heading: headings.firacIssue, blocks: issueBlocks(input, labels) },
    part(input.rule, prompts.rule, headings.firacRule),
    part(input.reasoning, prompts.reasoning, headings.firacApplication),
    {
      heading: headings.firacConclusion,
      blocks: [
        ...paragraphsOrPrompt(input.holding, prompts.holding),
        { kind: "subheading", text: headings.disposition },
        ...paragraphsOrPrompt(input.disposition, prompts.disposition),
      ],
    },
    part(input.separate, prompts.separate, headings.separate),
    part(input.notes, prompts.notes, headings.notes),
  ];
}

export function buildCaseBrief(
  input: CaseBriefInput,
  labels: CaseBriefLabels
): DocumentModel {
  const name = input.caseName.trim();
  return {
    title: name ? [{ text: name, italic: true }] : labels.defaultTitle,
    subtitle: labels.subtitle[input.layout],
    sections: [
      {
        blocks: [
          caseFields(input, labels),
          { kind: "guidance", text: labels.citationNote },
        ],
      },
      ...(input.layout === "firac"
        ? firac(input, labels)
        : standard(input, labels)),
    ],
  };
}
